import React, { useState } from 'react';
import { Card, Typography, Form, Input, Button, Rate, Select, message, Space, Alert, Tabs, Tag, Progress, Divider } from 'antd';
import { SendOutlined, SmileOutlined, MehOutlined, FrownOutlined, StarOutlined, MessageOutlined, BugOutlined, BulbOutlined, HeartOutlined } from '@ant-design/icons';
import api from '../../utils/axios';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

interface FeedbackForm {
  type: string;
  rating: number;
  title: string;
  description: string;
  category: string;
  contactEmail?: string;
  priority?: 'low' | 'medium' | 'high';
  userType?: 'new' | 'regular' | 'power';
  featureRequest?: string;
  improvementArea?: string;
}

const customIcons: Record<number, React.ReactNode> = {
  1: <FrownOutlined />,
  2: <FrownOutlined />,
  3: <MehOutlined />,
  4: <SmileOutlined />,
  5: <SmileOutlined />,
};

const Feedback: React.FC = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('submit');

  const handleSubmit = async (values: FeedbackForm) => {
    setSubmitting(true);
    try {
      const response = await api.post<{ success: boolean }>('/feedback', values);
      if (response.data.success) {
        message.success('Thank you for your feedback! We appreciate your input and will review it carefully.');
        form.resetFields();
      } else {
        message.error('Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      message.error('Failed to submit feedback. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const feedbackStats = {
    totalSubmitted: 1247,
    averageRating: 4.2,
    responseRate: 98,
    implementationRate: 23
  };

  const recentFeedback = [
    {
      type: 'feature',
      title: 'Mobile app improvements',
      rating: 5,
      status: 'implemented',
      date: '2024-01-15'
    },
    {
      type: 'bug',
      title: 'Report generation issue',
      rating: 4,
      status: 'in-progress',
      date: '2024-01-14'
    },
    {
      type: 'improvement',
      title: 'Dashboard customization',
      rating: 5,
      status: 'planned',
      date: '2024-01-13'
    }
  ];

  const feedbackCategories = [
    {
      key: 'bug',
      title: 'Bug Report',
      description: 'Report issues or problems you\'ve encountered',
      icon: <BugOutlined className="text-red-500" />,
      color: 'red'
    },
    {
      key: 'feature',
      title: 'Feature Request',
      description: 'Suggest new features or capabilities',
      icon: <BulbOutlined className="text-blue-500" />,
      color: 'blue'
    },
    {
      key: 'improvement',
      title: 'Improvement Suggestion',
      description: 'Suggest improvements to existing features',
      icon: <StarOutlined className="text-green-500" />,
      color: 'green'
    },
    {
      key: 'praise',
      title: 'Praise & Appreciation',
      description: 'Share positive feedback and appreciation',
      icon: <HeartOutlined className="text-pink-500" />,
      color: 'pink'
    },
    {
      key: 'other',
      title: 'Other',
      description: 'General feedback or suggestions',
      icon: <MessageOutlined className="text-gray-500" />,
      color: 'gray'
    }
  ];

  return (
    <div className="p-6">
      <Card>
        <div className="mb-6">
          <Title level={3}>Feedback Center</Title>
          <Text type="secondary">
            We value your feedback! Your input helps us improve and provide better service to all users.
          </Text>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
          <TabPane tab="Submit Feedback" key="submit">
            <div className="max-w-4xl mx-auto">
              <Alert
                message="Your feedback is important to us"
                description="We review all feedback and use it to improve our application. Thank you for taking the time to share your thoughts."
                type="info"
                showIcon
                className="mb-6"
              />

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Form.Item
                    name="type"
                    label="Feedback Type"
                    rules={[{ required: true, message: 'Please select feedback type' }]}
                  >
                    <Select placeholder="Select feedback type" size="large">
                      {feedbackCategories.map(category => (
                        <Option key={category.key} value={category.key}>
                          <div className="flex items-center">
                            {category.icon}
                            <span className="ml-2">{category.title}</span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="category"
                    label="Category"
                    rules={[{ required: true, message: 'Please select a category' }]}
                  >
                    <Select placeholder="Select category" size="large">
                      <Option value="ui">User Interface</Option>
                      <Option value="ux">User Experience</Option>
                      <Option value="performance">Performance</Option>
                      <Option value="functionality">Functionality</Option>
                      <Option value="documentation">Documentation</Option>
                      <Option value="mobile">Mobile App</Option>
                      <Option value="reports">Reports & Analytics</Option>
                      <Option value="integration">Integrations</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>
                </div>

                <Form.Item
                  name="rating"
                  label="How would you rate your overall experience?"
                  rules={[{ required: true, message: 'Please provide a rating' }]}
                >
                  <div className="space-y-2">
                    <Rate
                      character={({ index }) => customIcons[index! + 1]}
                      tooltips={['Very Dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very Satisfied']}
                      size="large"
                    />
                    <Text type="secondary">Click to rate your experience</Text>
                  </div>
                </Form.Item>

                <Form.Item
                  name="title"
                  label="Title"
                  rules={[{ required: true, message: 'Please enter a title' }]}
                >
                  <Input placeholder="Brief summary of your feedback" size="large" />
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Detailed Description"
                  rules={[{ required: true, message: 'Please provide a description' }]}
                >
                  <TextArea
                    rows={6}
                    placeholder="Please provide detailed feedback. Include any specific examples, steps to reproduce issues, or suggestions for improvement."
                    size="large"
                  />
                </Form.Item>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Form.Item
                    name="priority"
                    label="Priority Level"
                  >
                    <Select placeholder="Select priority" size="large">
                      <Option value="low">Low - Nice to have</Option>
                      <Option value="medium">Medium - Important</Option>
                      <Option value="high">High - Critical</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="userType"
                    label="User Type"
                  >
                    <Select placeholder="Select user type" size="large">
                      <Option value="new">New User (0-3 months)</Option>
                      <Option value="regular">Regular User (3-12 months)</Option>
                      <Option value="power">Power User (1+ years)</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="contactEmail"
                    label="Contact Email (Optional)"
                    rules={[
                      { type: 'email', message: 'Please enter a valid email address' },
                    ]}
                  >
                    <Input placeholder="Enter your email if you'd like us to follow up" size="large" />
                  </Form.Item>
                </div>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SendOutlined />}
                    loading={submitting}
                    size="large"
                    block
                  >
                    Submit Feedback
                  </Button>
                </Form.Item>
              </Form>

              <Divider />

              <div className="space-y-4">
                <Title level={5}>What happens with your feedback?</Title>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="bg-blue-100 p-2 rounded-full mr-3">
                        <MessageOutlined className="text-blue-500" />
                      </div>
                      <div>
                        <Text strong>Review Process</Text>
                        <br />
                        <Text type="secondary">All feedback is reviewed by our team within 48 hours</Text>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-red-100 p-2 rounded-full mr-3">
                        <BugOutlined className="text-red-500" />
                      </div>
                      <div>
                        <Text strong>Bug Reports</Text>
                        <br />
                        <Text type="secondary">Bug reports are prioritized based on severity and impact</Text>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-green-100 p-2 rounded-full mr-3">
                        <BulbOutlined className="text-green-500" />
                      </div>
                      <div>
                        <Text strong>Feature Requests</Text>
                        <br />
                        <Text type="secondary">Feature requests are evaluated for future releases</Text>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="bg-purple-100 p-2 rounded-full mr-3">
                        <StarOutlined className="text-purple-500" />
                      </div>
                      <div>
                        <Text strong>Improvement Suggestions</Text>
                        <br />
                        <Text type="secondary">Improvement suggestions are considered for updates</Text>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-orange-100 p-2 rounded-full mr-3">
                        <MessageOutlined className="text-orange-500" />
                      </div>
                      <div>
                        <Text strong>Follow-up Communication</Text>
                        <br />
                        <Text type="secondary">We may contact you for more information if needed</Text>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-pink-100 p-2 rounded-full mr-3">
                        <HeartOutlined className="text-pink-500" />
                      </div>
                      <div>
                        <Text strong>Community Impact</Text>
                        <br />
                        <Text type="secondary">Your feedback helps improve the experience for all users</Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>

          <TabPane tab="Feedback Stats" key="stats">
            <div className="space-y-6">
              <Title level={4}>Feedback Statistics</Title>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card size="small">
                  <div className="text-center">
                    <Title level={3} className="text-blue-500">{feedbackStats.totalSubmitted}</Title>
                    <Text type="secondary">Total Feedback Submitted</Text>
                  </div>
                </Card>
                <Card size="small">
                  <div className="text-center">
                    <Title level={3} className="text-green-500">{feedbackStats.averageRating}/5</Title>
                    <Text type="secondary">Average Rating</Text>
                  </div>
                </Card>
                <Card size="small">
                  <div className="text-center">
                    <Title level={3} className="text-orange-500">{feedbackStats.responseRate}%</Title>
                    <Text type="secondary">Response Rate</Text>
                  </div>
                </Card>
                <Card size="small">
                  <div className="text-center">
                    <Title level={3} className="text-purple-500">{feedbackStats.implementationRate}%</Title>
                    <Text type="secondary">Implementation Rate</Text>
                  </div>
                </Card>
              </div>

              <Divider />

              <Title level={5}>Recent Feedback</Title>
              <div className="space-y-3">
                {recentFeedback.map((feedback, index) => (
                  <Card key={index} size="small">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Tag color={
                          feedback.status === 'implemented' ? 'green' :
                          feedback.status === 'in-progress' ? 'blue' : 'orange'
                        }>
                          {feedback.status}
                        </Tag>
                        <div className="ml-3">
                          <Text strong>{feedback.title}</Text>
                          <br />
                          <Text type="secondary">{feedback.date}</Text>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Rate disabled defaultValue={feedback.rating} size="small" />
                        <Tag color="blue" className="ml-2">{feedback.type}</Tag>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </TabPane>

          <TabPane tab="Feedback Guidelines" key="guidelines">
            <div className="space-y-6">
              <Title level={4}>How to Write Effective Feedback</Title>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Title level={5}>For Bug Reports</Title>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Describe the problem clearly and concisely</li>
                    <li>Include steps to reproduce the issue</li>
                    <li>Mention your browser and device information</li>
                    <li>Add screenshots or screen recordings if possible</li>
                    <li>Note when the problem first occurred</li>
                  </ul>
                </div>
                
                <div>
                  <Title level={5}>For Feature Requests</Title>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Explain the problem you're trying to solve</li>
                    <li>Describe your ideal solution</li>
                    <li>Mention any workarounds you currently use</li>
                    <li>Consider the impact on other users</li>
                    <li>Be specific about your use case</li>
                  </ul>
                </div>
                
                <div>
                  <Title level={5}>For Improvement Suggestions</Title>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Focus on the user experience</li>
                    <li>Explain why the change would be beneficial</li>
                    <li>Consider the technical feasibility</li>
                    <li>Provide specific examples</li>
                    <li>Think about the broader impact</li>
                  </ul>
                </div>
                
                <div>
                  <Title level={5}>For Praise & Appreciation</Title>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Be specific about what you appreciate</li>
                    <li>Mention how it helps your workflow</li>
                    <li>Share any positive outcomes</li>
                    <li>Suggest ways to spread the word</li>
                    <li>Consider sharing your story</li>
                  </ul>
                </div>
              </div>

              <Divider />

              <Alert
                message="Privacy & Confidentiality"
                description="Your feedback is treated with confidentiality. We may share anonymized feedback with our development team, but personal information is never shared without your explicit consent."
                type="info"
                showIcon
              />
            </div>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default Feedback; 